/**
 * Simplified PDF Save Hook
 *
 * Consolidates all PDF save operations into 2 batched mutations:
 * 1. SavePdfFormFieldsMutation - for form fields (textfields, checkboxes)
 * 2. SavePdfAnnotationsMutation - for annotations (comments, strikeouts, free text)
 */

import { useCallback, useRef } from 'react';
import { useMutation } from '@apollo/client';
import {
  SavePdfFormFieldsMutation,
  SavePdfAnnotationsMutation,
  saveTemplatePdfFormFields,
  saveTemplatePdfAnnotations,
} from '../../../api/mutations';
import { FormField, PDFComment, PDFStrikeout, PDFFreeText, ResourceType } from '../types';

interface UsePdfSaveOptions {
  documentId: string;
  resourceType?: ResourceType; // 'document' (default) or 'template'
  onSaveSuccess?: (timestamp?: string) => void;
  onSaveError?: (error: Error) => void;
  debounceMs?: number;
}

interface UsePdfSaveResult {
  saveAll: (
    formFields: FormField[],
    comments: PDFComment[],
    strikeouts: PDFStrikeout[],
    freeTexts: PDFFreeText[]
  ) => void;
  savingFormFields: boolean;
  savingAnnotations: boolean;
  saving: boolean;
}

/**
 * Hook for saving PDF form fields and annotations with debouncing
 */
export const usePdfSave = ({
  documentId,
  resourceType = 'document',
  onSaveSuccess,
  onSaveError,
  debounceMs = 1000,
}: UsePdfSaveOptions): UsePdfSaveResult => {
  // Choose mutations based on resource type
  const isTemplate = resourceType === 'template';

  const [saveFormFields, { loading: savingFormFields }] = useMutation(
    isTemplate ? saveTemplatePdfFormFields : SavePdfFormFieldsMutation,
    {
      onError: (error) => onSaveError?.(error),
    }
  );

  const [saveAnnotations, { loading: savingAnnotations }] = useMutation(
    isTemplate ? saveTemplatePdfAnnotations : SavePdfAnnotationsMutation,
    {
      onError: (error) => onSaveError?.(error),
    }
  );

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Save all PDF data (form fields + annotations) with debouncing
   * Batches all changes together and sends in 2 mutations
   */
  const saveAll = useCallback(
    (
      formFields: FormField[],
      comments: PDFComment[],
      strikeouts: PDFStrikeout[],
      freeTexts: PDFFreeText[]
    ) => {
      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce the save
      debounceTimerRef.current = setTimeout(async () => {
        try {
          let updatedTimestamp: string | undefined;

          // Save form fields (always save, even if empty, to persist deletions)
          const fieldsMapped = formFields.map((field) => ({
            id: field.id,
            type: field.type,
            page: field.pageNum, // TWO-PHASE: Use pageNum (derived from pageId) for backend
            x: field.x,
            y: field.y,
            width: field.width,
            height: field.height,
            value: field.value,
            fieldName: field.fieldName,
            placeholder: field.placeholder,
            // Checkboxes should never have alignment properties - always centered
            horizontalAlign: field.type === 'checkbox' ? undefined : field.horizontalAlign,
            verticalAlign: field.type === 'checkbox' ? undefined : field.verticalAlign,
            multiline: field.multiline,
            fontSize: field.fontSize,
            fontColor: field.fontColor,
          }));

          // Templates use documentTemplateId, documents use documentId
          const result = await saveFormFields({
            variables: isTemplate
              ? { documentTemplateId: documentId, fields: fieldsMapped }
              : { documentId, fields: fieldsMapped },
          } as any);

          // Capture timestamp from mutation response (updatedAt for templates, lastUpdated for documents)
          updatedTimestamp = isTemplate
            ? (result.data as any)?.saveTemplatePdfFormFields?.updatedAt
            : (result.data as any)?.savePdfFormFields?.lastUpdated;

          // Consolidate all annotations into single array
          const allAnnotations = [
            // Filter out comments with empty text before saving
            ...comments
              .filter(c => c.text && c.text.trim() !== '')
              .map((c) => ({
                id: c.id,
                type: 'comment' as const,
                page: c.pageNum,
                x: c.x,
                y: c.y,
                text: c.text,
                author: c.author,
                authorId: c.authorId,
                createdAt: c.createdAt,
                parentId: c.parentId, // Preserve parent relationship for threaded replies
              })),
            ...strikeouts.map((s) => ({
              id: s.id,
              type: 'strikeout' as const,
              page: s.pageNum,
              x: s.x,
              y: s.y,
              width: s.width,
              color: s.color,
              groupId: s.groupId,
            })),
            ...freeTexts.map((f) => ({
              id: f.id,
              type: 'freetext' as const,
              page: f.pageNum,
              x: f.x,
              y: f.y,
              width: f.width,
              height: f.height,
              text: f.text,
              fontSize: f.fontSize,
              fontColor: f.fontColor,
            })),
          ];

          // Save annotations (ALWAYS save, even if empty, to persist deletions)
          // Templates use documentTemplateId, documents use documentId
          const annotationsResult = await saveAnnotations({
            variables: isTemplate
              ? { documentTemplateId: documentId, annotations: allAnnotations }
              : { documentId, annotations: allAnnotations },
          } as any);

          // Capture timestamp from mutation response (prefer annotations timestamp if both mutations ran)
          updatedTimestamp = isTemplate
            ? (annotationsResult.data as any)?.saveTemplatePdfAnnotations?.updatedAt
            : (annotationsResult.data as any)?.savePdfAnnotations?.lastUpdated;

          // Call onSaveSuccess with timestamp from mutation response (no refetch needed!)
          onSaveSuccess?.(updatedTimestamp);
        } catch (error) {
          console.error('Error saving PDF data:', error);
          onSaveError?.(error as Error);
        }
      }, debounceMs);
    },
    [documentId, isTemplate, saveFormFields, saveAnnotations, debounceMs, onSaveSuccess, onSaveError]
  );

  return {
    saveAll,
    savingFormFields,
    savingAnnotations,
    saving: savingFormFields || savingAnnotations,
  };
};
